// =============================================================================
// hooks/sales/useCampaigns.ts — Hook de leitura de campanhas comerciais
// (`sales_campaigns`, F35-S02/F35-S07, doc 26 §6.5/§9).
//
// Camada de dados REUTILIZÁVEL — vive fora da árvore visual de
// features/sales/campaigns/ (mesmo padrão de hooks/sales/useAppointments.ts,
// doc 27 §3.1) para que F35-S09/F35-S10 consultem a mesma lista/detalhe sem
// reimplementar a busca.
//
// Contrato: `Campaign`/`CampaignListQuery`/`CampaignListResponse` vêm de
// `@elemento/shared-schemas` (packages/shared-schemas/src/sales-campaigns.ts)
// — a MESMA fonte que a API usa para validar request/response. Nenhum campo
// é redeclarado aqui.
//
// LIMITAÇÃO CONHECIDA (documentada para o board, não escondida): o endpoint
// real `GET /api/sales/campaigns` (F35-S02, já em produção) devolve só o
// registro bruto de `sales_campaigns` — nome, status, prioridade, produto,
// proprietário, datas. Os indicadores agregados que o doc 26 §6.5 pede
// ("pessoas a trabalhar", "retornos hoje", "taxa de contato", "conversões")
// e os campos de linha ("público", "progresso", "próxima janela", "alertas")
// dependem de `campaign_members`/`contact_attempts`, que NENHUM slot
// mesclado em `main` agrega hoje (F35-S03/S04 — público/higiene/membros —
// seguem `blocked`; não existe endpoint de sumário). Por isso este hook NÃO
// inventa esses números no cliente (proibido por norma — "sem cálculo
// duplicado"): expõe só o que a API realmente garante, e
// `features/sales/campaigns/*` marca as colunas/indicadores pendentes como
// "Indisponível" em vez de fabricar dado. Ver nota em CampaignsIndicators.tsx
// e CampaignsList.tsx. Quando um slot de backend publicar o agregado, a
// extensão entra aqui (useCampaignsSummary) sem tocar os componentes visuais.
// =============================================================================

import type { Campaign, CampaignListQuery, CampaignListResponse } from '@elemento/shared-schemas';
import { CampaignListResponseSchema } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';

export type { Campaign, CampaignListQuery, CampaignListResponse };

// ---------------------------------------------------------------------------
// Filtros aceitos pelo hook — subconjunto de CampaignListQuery que a UI
// efetivamente monta.
// ---------------------------------------------------------------------------

export interface CampaignsFilters {
  status?: CampaignListQuery['status'];
  ownerUserId?: CampaignListQuery['ownerUserId'];
  productId?: CampaignListQuery['productId'];
  createdFrom?: string;
  createdTo?: string;
  sortBy?: CampaignListQuery['sortBy'];
  sortOrder?: CampaignListQuery['sortOrder'];
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const campaignKeys = {
  all: ['sales', 'campaigns'] as const,
  list: (filters: CampaignsFilters) => ['sales', 'campaigns', 'list', filters] as const,
};

// ---------------------------------------------------------------------------
// Querystring — array params seguem a convenção scalar→array do backend
// (packages/shared-schemas/src/sales-campaigns.ts arrayQueryParam): múltiplos
// valores viram múltiplas entradas `?status=a&status=b`, nunca CSV.
// ---------------------------------------------------------------------------

export function buildCampaignsQuery(filters: CampaignsFilters): string {
  const qs = new URLSearchParams();

  for (const s of filters.status ?? []) qs.append('status', s);
  for (const id of filters.ownerUserId ?? []) qs.append('ownerUserId', id);
  for (const id of filters.productId ?? []) qs.append('productId', id);

  if (filters.createdFrom !== undefined) qs.set('createdFrom', filters.createdFrom);
  if (filters.createdTo !== undefined) qs.set('createdTo', filters.createdTo);
  if (filters.sortBy !== undefined) qs.set('sortBy', filters.sortBy);
  if (filters.sortOrder !== undefined) qs.set('sortOrder', filters.sortOrder);
  if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
  if (filters.offset !== undefined) qs.set('offset', String(filters.offset));

  return qs.toString();
}

// ---------------------------------------------------------------------------
// Fetcher
// ---------------------------------------------------------------------------

async function fetchCampaigns(filters: CampaignsFilters): Promise<CampaignListResponse> {
  const query = buildCampaignsQuery(filters);
  const raw = await api.get<unknown>(`/api/sales/campaigns${query ? `?${query}` : ''}`);
  // Validação runtime: parse defensivo evita que um drift de contrato
  // silencioso vire tela quebrada (doc 26 §9, mesmo padrão de useAgents.ts).
  return CampaignListResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// useCampaigns — lista paginada com filtros
// ---------------------------------------------------------------------------

export interface UseCampaignsOptions {
  /** Desabilita o fetch (ex: aguardando gate de permissão/flag). Default true. */
  enabled?: boolean;
}

export function useCampaigns(
  filters: CampaignsFilters,
  opts: UseCampaignsOptions = {},
): {
  data: CampaignListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: campaignKeys.list(filters),
    queryFn: () => fetchCampaigns(filters),
    enabled: opts.enabled ?? true,
    placeholderData: (prev) => prev,
  });

  return { data, isLoading, isError, error: error as Error | null, refetch };
}

// ---------------------------------------------------------------------------
// useCampaignsCount — total de um recorte, sem carregar a lista inteira
// (limit=1 — só o campo `total` importa). Fonte 100% real da API — usado
// pelo indicador "Ativas" da faixa de KPIs (CampaignsIndicators.tsx).
// ---------------------------------------------------------------------------

export function useCampaignsCount(
  filters: Omit<CampaignsFilters, 'limit' | 'offset'>,
  opts: UseCampaignsOptions = {},
): { total: number | undefined; isLoading: boolean; isError: boolean } {
  const countFilters: CampaignsFilters = { ...filters, limit: 1, offset: 0 };
  const { data, isLoading, isError } = useQuery({
    queryKey: campaignKeys.list(countFilters),
    queryFn: () => fetchCampaigns(countFilters),
    enabled: opts.enabled ?? true,
    placeholderData: (prev) => prev,
  });

  return { total: data?.total, isLoading, isError };
}
